import { collection, addDoc, getDocs, doc, updateDoc, query, where, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase-config';
import { Comment, CommentInput } from '../../../models/Comment';

const COMMENTS_COLLECTION = 'comments';

export class CommentsService {
  static async addComment(commentInput: CommentInput, userId: string, userName: string, userProfilePicture?: string, userEmail?: string, userPhone?: string): Promise<Comment> {
    try {
      const isGuest = userId.startsWith('guest-');
      const commentData = {
        ...commentInput,
        userId,
        userName,
        userProfilePicture: userProfilePicture || '',
        ...(userEmail && { userEmail }), // Only add email for guest comments
        ...(userPhone && { userPhone }), // Only add phone for guest comments
        timestamp: Timestamp.now(),
        status: 'active' as const,
        likes: 0,
        ...(isGuest && { isGuest: true }), // Mark as guest comment
      };

      const docRef = await addDoc(collection(db, COMMENTS_COLLECTION), commentData);
      return {
        commentId: docRef.id,
        ...commentData,
        timestamp: commentData.timestamp.toDate(),
      };
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  }

  static async getCommentsForProfile(
    profileId: string, 
    page: number = 1, 
    pageSize: number = 5
  ): Promise<{comments: Comment[], totalCount: number, hasMore: boolean}> {
    try {
      // First, get total count (we'll limit this to avoid too many reads)
      const countQuery = query(
        collection(db, COMMENTS_COLLECTION),
        where('profileId', '==', profileId),
        where('status', '==', 'active')
      );
      const countSnapshot = await getDocs(countQuery);
      const totalCount = Math.min(countSnapshot.size, 100); // Cap at 100 for performance

      // Calculate offset for pagination
      const offset = (page - 1) * pageSize;
      
      // Get paginated comments
      const commentsQuery = query(
        collection(db, COMMENTS_COLLECTION),
        where('profileId', '==', profileId),
        where('status', '==', 'active'),
        orderBy('timestamp', 'desc'),
        limit(offset + pageSize)
      );

      const querySnapshot = await getDocs(commentsQuery);
      const allComments = querySnapshot.docs.map(doc => ({
        commentId: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp.toDate(),
      })) as Comment[];

      // Extract the current page
      const comments = allComments.slice(offset, offset + pageSize);
      const hasMore = offset + pageSize < totalCount;

      return { comments, totalCount, hasMore };
    } catch (error) {
      console.error('Error getting comments:', error);
      throw error;
    }
  }

  static async updateComment(commentId: string, updates: Partial<Comment>): Promise<void> {
    try {
      const commentRef = doc(db, COMMENTS_COLLECTION, commentId);
      await updateDoc(commentRef, updates);
    } catch (error) {
      console.error('Error updating comment:', error);
      throw error;
    }
  }

  static async deleteComment(commentId: string): Promise<void> {
    try {
      const commentRef = doc(db, COMMENTS_COLLECTION, commentId);
      await updateDoc(commentRef, { status: 'deleted' });
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  }
}